import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { History, Smartphone, Laptop, MapPin, RefreshCw, ShieldCheck } from "lucide-react";

const fmt = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso || "-"; } };
const isMobile = (d) => /HP|Tablet|Android|iPhone|iPad/i.test(d || "");

export default function LoginHistory() {
  const [rows, setRows] = useState([]);
  const [maxDevices, setMaxDevices] = useState(2);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const r = await api.get("/auth/sessions");
      setRows(r.data?.sessions || []);
      setMaxDevices(r.data?.max_devices || 2);
    } catch { toast.error("Gagal memuat riwayat login"); }
    finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const activeCount = rows.filter((r) => r.active).length;

  return (
    <div className="p-4 sm:p-8 max-w-[900px] mx-auto" data-testid="login-history-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-cyan-400/90 flex items-center gap-2"><History className="h-4 w-4" /> Keamanan</div>
          <h1 className="font-head font-black text-4xl uppercase tracking-tight mt-1">Riwayat Login</h1>
          <p className="text-slate-500 text-sm mt-1">Lokasi, negara & perangkat yang dipakai untuk login. Maksimal <b className="text-cyan-400">{maxDevices} perangkat</b> aktif — login di perangkat ke-{maxDevices + 1} otomatis mengeluarkan perangkat terlama.</p>
        </div>
        <button data-testid="login-history-refresh" onClick={load} disabled={busy} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-700 text-slate-200 hover:bg-slate-800 text-sm font-semibold">
          <RefreshCw className={`h-4 w-4 ${busy ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      <div className="mb-4 inline-flex items-center gap-2 text-sm text-slate-300 bg-[#0c1017] border border-cyan-400/30 rounded-lg px-3 py-2" data-testid="login-history-active-count">
        <ShieldCheck className="h-4 w-4 text-cyan-400" /> Perangkat aktif: <b className="text-cyan-400">{activeCount}</b> / {maxDevices}
      </div>

      <div className="space-y-3">
        {rows.length === 0 && !busy && <div className="text-slate-500 text-sm" data-testid="login-history-empty">Belum ada riwayat login.</div>}
        {rows.map((s, i) => (
          <div key={s.sid || i} data-testid={`login-history-row-${i}`}
            className={`rounded-xl border p-4 flex items-start gap-3 ${s.current ? "border-cyan-400/60 bg-cyan-400/5" : s.active ? "border-slate-700 bg-[#0c1017]" : "border-slate-800 bg-[#0a0e16] opacity-70"}`}>
            <div className={`shrink-0 h-10 w-10 rounded-lg grid place-items-center ${s.active ? "bg-cyan-400/15 text-cyan-300" : "bg-slate-800 text-slate-500"}`}>
              {isMobile(s.device) ? <Smartphone className="h-5 w-5" /> : <Laptop className="h-5 w-5" />}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-bold text-slate-100" data-testid={`login-history-device-${i}`}>{s.device || "Perangkat"}</span>
                {s.browser && <span className="text-xs text-slate-400">· {s.browser}</span>}
                {s.current && <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded bg-cyan-400 text-slate-950">Perangkat ini</span>}
                {!s.current && s.active && <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-300">Aktif</span>}
                {!s.active && <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded bg-slate-700 text-slate-300">Keluar{ s.ended_reason === "device_limit" ? " (batas perangkat)" : ""}</span>}
              </div>
              <div className="text-sm text-slate-400 mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                <span className="inline-flex items-center gap-1" data-testid={`login-history-loc-${i}`}>
                  <MapPin className="h-3.5 w-3.5 text-slate-500" />
                  {[s.city, s.region, s.country].filter(Boolean).join(", ") || "Lokasi tidak diketahui"}
                  {s.country_code ? ` (${s.country_code})` : ""}
                </span>
                {s.ip && <span className="font-mono text-xs text-slate-500">IP {s.ip}</span>}
              </div>
              <div className="text-xs text-slate-500 mt-1">Login: {fmt(s.created_at)} · Aktivitas terakhir: {fmt(s.last_seen)}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
